import { useEffect, useRef, useState } from "react";
import { resolveInitialPayerPhone } from "../utils/deliveryPhone";
import {
  formatCurrency,
  isFwMomoMethod,
  fwNetworkFor,
} from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { linesSubtotal, meetsMinimumOrder } from "../utils/minimumOrder";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Table, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@mui/material/Container";
import momo from "../assets/img/momo.jpg";
import airtel from "../assets/img/airtel_money.png";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Divider, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Image } from "react-bootstrap";
import styled from "styled-components";

import {
  createOrder,
  payAirtelMoneyCheckout,
  payMoMoCheckout,
  payCardCheckout,
} from "../redux/healthcareOrderActions.js";
import HealthcareNavBar from "../components/HealthcareNavBar";
import CheckoutStepper from "../components/CheckoutStepper";
import useDeliveryFee from "../redux/useDeliveryFee";
import DeliveryFeeStatus from "../components/DeliveryFeeStatus";
import FreightCheckoutNotice from "../components/FreightCheckoutNotice";
import { newIdempotencyKey } from "../utils/idempotency";
import CreditCardForm from "../components/CreditCardForm/CreditCardForm";
import { clearCart } from "../redux/healthcareCartSlice";
import { extractCreatedOrderId } from "../redux/checkout";
import { followCardRedirect } from "../utils/cardPayment";
import { apiSetContactEmail } from "../api/profileApi";
import { toast } from "react-toastify";
import { StruckPrice, VendorOfferAmount } from "../components/PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import useCheckoutDeliverySchedule from "../hooks/useCheckoutDeliverySchedule";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import { cartOutletId } from "../utils/vendorOutlets";
import DeliveryTimePicker from "../components/DeliveryTimePicker";

const PayCardButton = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: #E91E63;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const AirtelPayButton = styled.button`
  border: none;
  padding: 7px 15px;
  background-color: #E91E63;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 10px;
  justify-content: center;
  align-items: center;
  display: flex;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

// Optional email capture at checkout. Most Lusuku customers register by PHONE
// and have no email, so the receipt has nowhere to go. Ask, never require.
const OptionalTag = styled.span`
  color: #9ca3af;
  font-size: 12px;
  font-weight: 500;
`;

const EmailHint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin: -4px 0 10px;
`;

const useStyles = makeStyles((theme) => ({
  input: {
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
      paddingBottom: "5px",
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
}));

const validationSchema = Yup.object({
  cardNumber: Yup.string()
    .min(16, "Enter a valid card number")
    .required("Card Number is required"),
  expiry_month: Yup.string().required("Expiry month is required"),
  expiry_year: Yup.string().required("Expiry year is required"),
  cardCvv: Yup.string()
    .min(3, "CVV must be at least 3 characters")
    .required("CVV is required"),
  fullName: Yup.string().required("Full name is required"),
});

function HealthcarePlaceOrderScreen() {
  const { vendorId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const { shippingAddress, paymentMethod, total } = useSelector(
    (state) => state.healthcareCart
  );

  const cart = useSelector((state) => state.healthcareCart.carts[vendorId] || {});
  const vendor = useSelector((state) => state.healthcareCart.vendor);
  const products = Object.values(cart);
  /* The pharmacy branch this cart is fulfilled from (4 Oct 2026), or null
     for the pharmacy itself: one branch per cart, read off its lines. The fee
     quote, the delivery slots and every order line name it, so the rider is
     sent to the branch that has the stock. */
  const outletId = cartOutletId(products);
  // What the vendor's live offers took off; the subtotal is already net of it.
  const offerSavings = cartOfferSavings(products);

  /* Second line of defence on the minimum order value. The cart says it first
     and the server is the gate that counts; this stops a deep link or a tab
     left open since before the floor existed from posting a basket the server
     will only refuse after the customer has entered a phone number. */
  const minimumOrderValue = useMinimumOrderValue();
  const belowMinimumOrder = !meetsMinimumOrder(
    linesSubtotal(products),
    minimumOrderValue
  );

  const user = useSelector((state) => state.user.currentUser);
  const { orderCreationStatus } = useSelector(
    (state) => state.healthcareOrder
  );

  // CRITICAL: Set order source to "Web" to differentiate from mobile orders
  const [orderSource] = useState("Web");

  useEffect(() => {
    if (
      Object.keys(shippingAddress).length === 0 ||
      Object.keys(paymentMethod).length === 0 ||
      Object.keys(products).length === 0
    ) {
      history.push(`/`);
    }
  }, [shippingAddress, paymentMethod, products, history, vendorId]);

  // Server-authoritative delivery fee (distance-based, signed quote). Vendor id
  // is the owner User id (vendor.owner), matching the per-item vendorId used
  // throughout this screen.
  const {
    fee: serverShippingFee,
    distanceKm,
    quoteToken,
    isReady: deliveryFeeReady,
    isFailed: deliveryFeeFailed,
    isBlocked: deliveryFeeBlocked,
    error: deliveryFeeError,
    refresh: refreshDeliveryFee,
    fulfillment: quoteFulfillment,
    freightConsentRequired,
  } = useDeliveryFee({
    vendorId: vendor?.owner || vendor?._id || vendorId,
    outletId,
    customerLat: shippingAddress?.latLng?.lat,
    customerLng: shippingAddress?.latLng?.lng,
    items: products,
  });

  // Express or a scheduled window from this pharmacy's published hours (the
  // same vendor id the fee is quoted and the order is created with).
  const deliveryTime = useCheckoutDeliverySchedule({
    flow: "healthcare",
    vendorId: vendor?.owner || vendor?._id || vendorId,
    outletId,
  });

  /* The cart check at its branch (4 Oct 2026). On this page's load the cart
     is checked (a cart read back from localStorage, or moved, may hold a line
     the branch no longer sells at that price), and every create-order call
     below goes through guardSubmit: checked first when a line is
     unconfirmed, and a 409 BRANCH_LINES_CHANGED opens "Changes at <branch>"
     from the server's own lines. A failed check never blocks the order. */
  const branchCheck = useCartBranchCheck({
    vertical: "healthcare",
    cartId: vendorId,
    checkWhen: Boolean(vendorId),
  });
  const submitOrder = (thunk, payload) =>
    branchCheck.guardSubmit(() => deliveryTime.submitOrder(thunk, payload));

  // One idempotency key per checkout attempt (Contract §B4).
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  // Shared props for the fee/total cells (Contract §C3): failures are VISIBLE
  // with a Retry, never a permanent "Calculating…".
  const feeStatusProps = {
    isReady: deliveryFeeReady,
    isFailed: deliveryFeeFailed,
    isBlocked: deliveryFeeBlocked,
    error: deliveryFeeError,
    onRetry: refreshDeliveryFee,
  };
  // Display-only total — the server recomputes the charged total on order-create.
  const totalOrderCostPlusShipping = Number(total) + Number(serverShippingFee);

  useEffect(() => {
    if (paymentMethod === "MOMO" && !isFwMomoMethod("MOMO") && orderCreationStatus === "fulfilled") {
      // Direct-MTN status page is Uganda-only; FW-momo markets redirect to Flutterwave.
      history.replace("/momoOrderPage");
    } else if (paymentMethod === "COD" && orderCreationStatus === "fulfilled") {
      history.replace("/healthcareOrderPage");
    }
  }, [orderCreationStatus, paymentMethod, history]);

  const formik = useFormik({
    initialValues: {
      cardNumber: "",
      expiry_month: "",
      expiry_year: "",
      cardCvv: "",
      cardType: "",
      fullName: "",
    },
    validationSchema,
    onSubmit: (values) => {
      cardPlaceOrderHandler(values);
    },
  });

  const [postData, setPostData] = useState({
    mobile: "",
  });  
  // Payer prefill: most customers pay with the number we deliver to (or their
  // account number), so seed the mobile-money field once instead of forcing a
  // retype. Fully editable for "someone else is paying", and the once-guard
  // means we never fight a customer who clears the field on purpose.
  const payerPrefilledRef = useRef(false);
  useEffect(() => {
    if (payerPrefilledRef.current) return;
    const prefill = resolveInitialPayerPhone(shippingAddress, user);
    if (prefill) {
      payerPrefilledRef.current = true;
      setPostData((d) => (d.mobile ? d : { ...d, mobile: prefill }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress, user]);


  const userId = user?.result?._id || user?._id;
  const userName = user?.result?.name || user?.result?.businessName || user?.name || user?.businessName;
  const userEmail = user?.result?.email || user?.email;
  // Optional email capture — phone-only customers have no address on file.
  // Never required: it must not gate any pay button.
  const [emailInput, setEmailInput] = useState("");
  const needsEmail = !userEmail;
  useEffect(() => {
    if (!userId) {
      history.push("/login");
    }
  }, [userId, history]);

  // COD Payment Handler - CRITICAL: Include orderSrc
  const placeOrderHandler = (e) => {
    e.preventDefault();
    // Use vendor.owner for the vendorId in orderItems (this is the user/owner ID)
    const ownerVendorId = vendor?.owner || vendor?._id;

    submitOrder(createOrder, {
      orderItems: products?.map((item) => ({
        ...item,
        vendorId: ownerVendorId, // Use owner ID, not healthcare document ID
        outletId: outletId || undefined,
        // Preserve healthcare-specific fields
        requiresPrescription: item.requiresPrescription,
        ageRestriction: item.ageRestriction,
        controlledSubstance: item.controlledSubstance,
        productType: item.productType,
      })),
      shippingAddress,
      // Server recomputes the fee/total; quote token lets it honour the
      // displayed fee. No client-computed fee is sent as authoritative.
      clientQuoteToken: quoteToken,
      distanceKm,
      paymentMethod,
      itemsPrice: total,
      totalPrice: totalOrderCostPlusShipping,
      customerName: userName,
      client_id: userId,
      orderSrc: orderSource,
      vendorId: ownerVendorId,
      qty: products?.map((item) => item.quantity),
      image: products?.map((item) => item.image),
      idempotencyKey: idempotencyKeyRef.current,
    }).then((action) => {
      // Only clear the cart once the order is actually persisted (Contract §C5).
      if (!action.error && action.meta?.requestStatus === "fulfilled") {
        dispatch(clearCart(vendorId));
      }
    });
  };

  // Canonical healthcare order items for create-order + payment payloads.
  const buildOrderItems = () => {
    const ownerVendorId = vendor?.owner || vendor?._id;
    return products.map((item) => ({
      ...item,
      name: item.name,
      vendorId: ownerVendorId,
      outletId: outletId || undefined,
      qty: item.quantity,
      price: item.price,
      image: item.image,
      requiresPrescription: item.requiresPrescription,
      ageRestriction: item.ageRestriction,
      controlledSubstance: item.controlledSubstance,
      productType: item.productType,
    }));
  };

  // Shared create-order payload (server recomputes the fee/total; the quote
  // token lets it honour the displayed fee — Contract §B1/§B2).
  const buildCreateOrderPayload = (orderItems) => ({
    orderItems,
    shippingAddress,
    clientQuoteToken: quoteToken,
    distanceKm,
    paymentMethod,
    itemsPrice: total,
    totalPrice: totalOrderCostPlusShipping,
    customerName: userName,
    client_id: userId,
    orderSrc: orderSource, // CRITICAL: Mark as Web order
    vendorId: vendor?.owner || vendor?._id, // Use owner ID
    idempotencyKey: idempotencyKeyRef.current,
  });

  const payMoMoCheckoutHandler = async (e) => {
    e.preventDefault();
    // MARKET RAIL SWITCH: ZM "MOMO" charges over the FW rail (network MTN).
    if (isFwMomoMethod("MOMO")) {
      return payAirtelMoneyCheckoutHandler(e, fwNetworkFor("MOMO"));
    }
    // Never charge before the server fee is known (Contract §C1).
    if (!deliveryFeeReady || freightConsentRequired) return;

    // WHY save but not send: no MoMo charge payload in this codebase carries an
    // email, so we only persist it to the profile — that is where the order
    // confirmation is addressed from. Best-effort; it must never block the pay.
    const typedEmail = emailInput.trim();
    if (!userEmail && typedEmail) {
      apiSetContactEmail(typedEmail).catch(() => {});
    }
    const orderItems = buildOrderItems();

    // Create the order FIRST and await it (Contract §B3) — never charge a
    // customer whose order failed (e.g. 422 OUT_OF_DELIVERY_RANGE).
    const orderAction = await submitOrder(createOrder, buildCreateOrderPayload(orderItems));
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      return;
    }
    const orderId = extractCreatedOrderId(orderAction);

    dispatch(
      payMoMoCheckout({
        mobile: postData.mobile,
        totalPrice: totalOrderCostPlusShipping,
        client_id: userId,
        orderItems,
        // Bind the payment to the order we just created (Contract §B3).
        orderId,
      })
    );
  };

  const generateTxRef = (clientId) => {
    return clientId + "-" + Date.now();
  };

  // Airtel Money Handler - CRITICAL: Include orderSrc
  const payAirtelMoneyCheckoutHandler = async (e, networkOverride) => {
    e.preventDefault();
    if (!deliveryFeeReady || freightConsentRequired) return; // Contract §C1 — no charge without a server fee
    const orderItems = buildOrderItems();

    // Create + await the order before charging (see payMoMoCheckoutHandler).
    const orderAction = await submitOrder(createOrder, buildCreateOrderPayload(orderItems));
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      return;
    }
    const orderId = extractCreatedOrderId(orderAction);
    // tx_ref = `${clientId}-${orderId}` so the Flutterwave webhook can bind the
    // payment to THIS order (Contract §B3).
    const uniqueTxRef = orderId ? `${userId}-${orderId}` : generateTxRef(userId);

    // Use whatever they typed for THIS charge and save it to the profile too.
    // Best-effort: a failed save must never stop the payment.
    const typed = emailInput.trim();
    const effectiveEmail = userEmail || typed || undefined;
    if (!userEmail && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }

    dispatch(
      // Payload field names follow the backend /fw/flutterAirtelPayment
      // contract: { mobile, totalPrice, email, network, redirect_url,
      // uniqueTxRef } — the previous amount/phoneNumber/tx_ref names were
      // silently dropped by the server (Contract §A1).
      payAirtelMoneyCheckout({
        mobile: postData.mobile,
        totalPrice: totalOrderCostPlusShipping,
        client_id: userId,
        email: effectiveEmail,
        uniqueTxRef,
        network: networkOverride || fwNetworkFor(paymentMethod) || paymentMethod,
        redirect_url: `${window.location.origin}/airtelPay`,
        orderItems,
        orderId,
      })
    ).then((action) => {
      if (!action.error && action.payload?.data?.meta?.authorization) {
        followCardRedirect(action.payload.redirectUrl);
      } else if (!action.error) {
        // The order already EXISTS: createOrder ran and was awaited above, so
        // silence here leaves the customer holding an unpaid order with no next
        // step and no idea anything went wrong. A REJECTED thunk has already
        // toasted the backend's own reason, so re-toasting it would just double
        // up; a 200 whose body carries no `meta.authorization` had no branch at
        // all, and that is the case this else was missing.
        toast.error(
          "We couldn't start the Airtel Money payment. Your order was created but has NOT been paid \u2014 open it in My Orders to try again.",
          { autoClose: false }
        );
      }
    });
  };

  // Card Payment Handler - CRITICAL: Include orderSrc
  const cardPlaceOrderHandler = async (values) => {
    if (!deliveryFeeReady || freightConsentRequired) return; // Contract §C1 — no charge without a server fee
    const orderItems = buildOrderItems();

    // Create + await the order before charging (see payMoMoCheckoutHandler).
    const orderAction = await submitOrder(createOrder, buildCreateOrderPayload(orderItems));
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      return;
    }
    const orderId = extractCreatedOrderId(orderAction);

    // Same optional-email treatment as Airtel (see payAirtelMoneyCheckoutHandler).
    const typed = emailInput.trim();
    const effectiveEmail = userEmail || typed || undefined;
    if (!userEmail && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }

    dispatch(
      // Payload follows the backend /fw/flutterCardPayment contract:
      // { cardDetails, totalPrice, email, client_id, redirect_url, orderId } —
      // the previous flat amount/cardNumber/cvv names were silently dropped by
      // the server (Contract §A1).
      payCardCheckout({
        cardDetails: {
          cardNumber: values.cardNumber,
          expiry_month: values.expiry_month,
          expiry_year: values.expiry_year,
          cardCvv: values.cardCvv,
          cardType: values.cardType,
          fullName: values.fullName,
        },
        totalPrice: totalOrderCostPlusShipping,
        client_id: userId,
        customer_name: userName,
        email: effectiveEmail,
        network: paymentMethod,
        redirect_url: `${window.location.origin}/cardOrder`,
        orderItems,
        orderId,
      })
    )
      .then((action) => {
        if (action.payload?.redirectUrl) {
          followCardRedirect(action.payload.redirectUrl);
        }
      })
      .catch((err) => {
        console.error("Error while dispatching payCardCheckout:", err);
      });
  };

  // Rendered inside each payment block below. Deliberately not `required`, not
  // validated, and absent from every disabled= expression: ask, never block.
  const optionalEmailField = needsEmail ? (
    <>
      <TextField
        label={
          <span>
            Email <OptionalTag>optional</OptionalTag>
          </span>
        }
        variant="outlined"
        type="email"
        fullWidth
        name="contactEmail"
        placeholder="you@example.com"
        autoComplete="email"
        value={emailInput}
        onChange={(ev) => setEmailInput(ev.target.value)}
        style={{ marginBottom: "10px" }}
      />
      <EmailHint>Add an email and we'll send your receipt there.</EmailHint>
    </>
  ) : null;

  const cartCount = Object.values(cart).reduce(
    (total, item) => total + item.quantity,
    0
  );

  // Redirect if vendor data is not loaded
  if (!vendor) {
    history.push(`/healthcare/${vendorId}`);
    return null;
  }

  return (
    <>
      <HealthcareNavBar vendor={vendor} cartCount={cartCount} />
      <BranchCartReviewDialog {...branchCheck.dialogProps} />
      <Container maxWidth="lg" style={{ marginTop: "100px", paddingBottom: "40px" }} sx={{ "@media (max-width: 850px)": { marginTop: "140px" } }}>
        <CheckoutStepper currentStep="confirm" />
        <FreightCheckoutNotice />
        <h1 style={{ color: "#E91E63", textAlign: "center", marginBottom: "30px" }}>
          Healthcare Order Review
        </h1>
        <Grid container spacing={4}>
          <Grid item lg={8} md={7} sm={12} xs={12}>
            <Card style={{ marginBottom: "20px" }}>
              <CardContent>
                <Typography variant="h6" style={{ color: "#E91E63", marginBottom: "15px" }}>
                  Shipping Information
                </Typography>
                <Divider />
                <Box mt={2}>
                  <Typography><strong>Phone:</strong> {shippingAddress.customerNumber}</Typography>
                  <Typography><strong>Address:</strong> {shippingAddress.locationName?.name}</Typography>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" style={{ color: "#E91E63", marginBottom: "15px" }}>
                  Order Items
                </Typography>
                <Divider />
                <Table style={{ marginTop: "15px" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell align="center"><strong>Quantity</strong></TableCell>
                      <TableCell align="right"><strong>Price</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
                            />
                            <div>
                              <div>{item.name}</div>
                              {item.requiresPrescription && (
                                <span style={{ fontSize: "0.75rem", color: "#d32f2f" }}>
                                  ℞ Prescription Required
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {lineHasDeal(item) && (
                            <StruckPrice>
                              {formatCurrency(item.listPrice * item.quantity)}
                            </StruckPrice>
                          )}
                          {formatCurrency(item.price * item.quantity)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <DeliveryTimePicker controller={deliveryTime} />
          </Grid>

          <Grid item lg={4} md={5} sm={12} xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" style={{ color: "#E91E63", marginBottom: "15px" }}>
                  Order Summary
                </Typography>
                <Divider />
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal:</Typography>
                    <Typography>{formatCurrency(total)}</Typography>
                  </Box>
                  {offerSavings > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography>Vendor offer:</Typography>
                      <VendorOfferAmount>-{formatCurrency(offerSavings)}</VendorOfferAmount>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Shipping:</Typography>
                    <Typography>
                      <DeliveryFeeStatus
                        {...feeStatusProps}
                        freightQuotedSeparately={quoteFulfillment?.required === true}
                        value={formatCurrency(serverShippingFee || 0)}
                      />
                    </Typography>
                  </Box>
                  <Divider style={{ margin: "10px 0" }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" style={{ fontWeight: "bold" }}>
                      Total:
                    </Typography>
                    <Typography variant="h6" style={{ fontWeight: "bold", color: "#E91E63" }}>
                      <DeliveryFeeStatus
                        {...feeStatusProps}
                        value={formatCurrency(totalOrderCostPlusShipping)}
                      />
                    </Typography>
                  </Box>
                </Box>

                <Box mt={3}>
                  <Typography variant="subtitle2" style={{ marginBottom: "10px" }}>
                    <strong>Payment Method: {paymentMethod}</strong>
                  </Typography>

                  {paymentMethod === "COD" && (
                    <PayCardButton
                      onClick={placeOrderHandler}
                      disabled={
                        !deliveryFeeReady ||
                        freightConsentRequired ||
                        belowMinimumOrder
                      }
                    >
                      {deliveryFeeReady
                        ? "Place Order (Cash on Delivery)"
                        : "Calculating delivery fee…"}
                    </PayCardButton>
                  )}

                  {paymentMethod === "MOMO" && (
                    <>
                      <Image src={momo} alt="MTN Mobile Money" style={{ width: "100%", marginBottom: "10px" }} />
                      <TextField
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        className={classes.input}
                        value={postData.mobile}
                        onChange={(e) => setPostData({ ...postData, mobile: e.target.value })}
                        style={{ marginBottom: "10px" }}
                      />
                      {optionalEmailField}
                      <PayCardButton
                        onClick={payMoMoCheckoutHandler}
                        disabled={
                          !deliveryFeeReady ||
                          freightConsentRequired ||
                          belowMinimumOrder ||
                          postData.mobile.length < 10 ||
                          orderCreationStatus === "pending"
                        }
                      >
                        Pay with MTN Mobile Money
                      </PayCardButton>
                    </>
                  )}

                  {(paymentMethod === "Airtel" || paymentMethod === "ZAMTEL") && (
                    <>
                      <Image src={airtel} alt="Airtel Money" style={{ width: "100%", marginBottom: "10px" }} />
                      <TextField
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        className={classes.input}
                        value={postData.mobile}
                        onChange={(e) => setPostData({ ...postData, mobile: e.target.value })}
                        style={{ marginBottom: "10px" }}
                      />
                      {optionalEmailField}
                      <AirtelPayButton
                        onClick={payAirtelMoneyCheckoutHandler}
                        disabled={
                          !deliveryFeeReady ||
                          freightConsentRequired ||
                          belowMinimumOrder ||
                          postData.mobile.length < 10 ||
                          orderCreationStatus === "pending"
                        }
                      >
                        Pay with Airtel Money
                      </AirtelPayButton>
                    </>
                  )}

                  {paymentMethod === "Visa" && (
                    <>
                      {optionalEmailField}
                      <CreditCardForm
                    onFormChange={(data) => {
                      /* The component takes `onFormChange` and nothing else.
                         This mount passed `formik`, so the handler it calls on
                         mount was undefined and card payment on this page threw
                         the moment it rendered. */
                      let expiry_month = "";
                      let expiry_year = "";
                      if (typeof data.expirationDate === "string") {
                        [expiry_month, expiry_year] =
                          data.expirationDate.split("/");
                      }
                      formik.setValues({
                        cardNumber: data.cardNumber,
                        expiry_month: expiry_month || "",
                        expiry_year: expiry_year || "",
                        cardCvv: data.cvv,
                        cardType: data.cardType,
                        fullName: data.fullName,
                      });
                    }}
                  />
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default HealthcarePlaceOrderScreen;
